import type { fabric } from 'fabric';
import type { FabricEditor } from '../core/fabric-editor';
import type { EditorPlugin } from '../interfaces/editor-plugin';
import { FabricPluginName } from './plugin-name';

export interface LayerFacade {
  bringForward(target?: fabric.Object): void;
  bringToFront(target?: fabric.Object): void;
  sendBackwards(target?: fabric.Object): void;
  sendToBack(target?: fabric.Object): void;
  canMoveUp(target?: fabric.Object): boolean;
  canMoveDown(target?: fabric.Object): boolean;
}

declare module '../core/fabric-editor' {
  interface FabricEditor {
    layers?: LayerFacade;
  }
}

export interface LayerPluginOptions {}

const isGrid = (obj: fabric.Object): boolean => (obj.data as { name?: string } | undefined)?.name === 'grid';

// A generic "pin to the top" flag, symmetric to the grid's bottom-pin. Any object
// carrying `data.pin === 'top'` (e.g. the manager's mandatory logo) is force-kept
// above every other object after any layer operation, and is excluded from being a
// reorder target so the layer buttons/hotkeys can never move a normal object above it.
const isTopPinned = (obj: fabric.Object): boolean =>
  (obj.data as { pin?: string } | undefined)?.pin === 'top';

export function layerPlugin(_options: LayerPluginOptions = {}): EditorPlugin {
  return {
    name: FabricPluginName.LAYER,
    install(editor: FabricEditor) {
      const canvas = editor.canvas;

      const resolveTargets = (target?: fabric.Object): fabric.Object[] => {
        const t = target ?? canvas.getActiveObject();
        if (!t) return [];
        // Why: only an ActiveSelection (Fabric's synthetic wrapper around a
        // marquee multi-selection) should expand into its members. A regular
        // fabric.Group — e.g. every placeholder — ALSO has a populated
        // `_objects`, but its children are NOT canvas objects. Expanding it
        // and calling canvas.bringToFront(child) would run `_objects.push(child)`
        // on the canvas, injecting the group's parts as loose top-level objects
        // at their group-relative (≈origin) coordinates — the placeholder
        // visibly "decomposes" and the pieces jump to the top-left corner.
        // Fabric's native bring*/send* already iterate an ActiveSelection's
        // members internally, so a real group is moved correctly as one unit.
        if (t.type === 'activeSelection') {
          const inner = (t as fabric.ActiveSelection)._objects;
          if (inner && inner.length > 0) return [...inner];
        }
        return [t];
      };

      const pinGrid = () => {
        for (const obj of canvas.getObjects()) {
          if (isGrid(obj)) canvas.sendToBack(obj);
        }
      };

      const pinTop = () => {
        for (const obj of canvas.getObjects()) {
          if (isTopPinned(obj)) canvas.bringToFront(obj);
        }
      };

      const sortByZ = (objs: fabric.Object[], ascending: boolean): fabric.Object[] => {
        const all = canvas.getObjects();
        const indexed = objs.map((o) => ({ obj: o, idx: all.indexOf(o) }));
        indexed.sort((a, b) => (ascending ? a.idx - b.idx : b.idx - a.idx));
        return indexed.map((e) => e.obj);
      };

      const apply = (target: fabric.Object | undefined, op: (o: fabric.Object) => void, descending: boolean) => {
        const targets = resolveTargets(target).filter((o) => !isGrid(o) && !isTopPinned(o));
        if (targets.length === 0) return;
        const ordered = sortByZ(targets, !descending);
        for (const o of ordered) op(o);
        pinGrid();
        pinTop();
        canvas.requestRenderAll();
        editor.emit('object:modified', { target: targets[0] });
      };

      const facade: LayerFacade = {
        bringForward(target) {
          apply(target, (o) => canvas.bringForward(o), true);
        },
        bringToFront(target) {
          apply(target, (o) => canvas.bringToFront(o), true);
        },
        sendBackwards(target) {
          apply(target, (o) => canvas.sendBackwards(o), false);
        },
        sendToBack(target) {
          apply(target, (o) => canvas.sendToBack(o), false);
        },
        canMoveUp(target) {
          const targets = resolveTargets(target).filter((o) => !isGrid(o) && !isTopPinned(o));
          if (targets.length === 0) return false;
          const all = canvas.getObjects();
          // The ceiling is the last object that is NOT pinned to the top, mirroring
          // canMoveDown's firstNonGrid floor. Without this, an object directly below a
          // top-pinned logo would report canMoveUp = true and the "up"/"front" buttons
          // and ctrl+] hotkey would wrongly move it above the logo.
          let lastNonPinnedTop = -1;
          for (let i = all.length - 1; i >= 0; i--) {
            if (!isTopPinned(all[i])) {
              lastNonPinnedTop = i;
              break;
            }
          }
          const topmostTarget = Math.max(...targets.map((o) => all.indexOf(o)));
          return topmostTarget < lastNonPinnedTop;
        },
        canMoveDown(target) {
          const targets = resolveTargets(target).filter((o) => !isGrid(o) && !isTopPinned(o));
          if (targets.length === 0) return false;
          const all = canvas.getObjects();
          const firstNonGrid = all.findIndex((o) => !isGrid(o));
          const bottomTarget = Math.min(...targets.map((o) => all.indexOf(o)));
          return bottomTarget > firstNonGrid;
        },
      };

      editor.layers = facade;
    },
    dispose(editor: FabricEditor) {
      editor.layers = undefined;
    },
  };
}
